/**
 * ConfirmDialog · reemplaza `window.confirm()` con UI MANAXER.
 *
 * Uso recomendado: vía el hook `useConfirm()` que expone una API similar
 * al confirm nativo (`await confirm({title, message})` → boolean) pero
 * renderiza el Modal estilizado. Mantener el confirm en sitios que ya
 * usaban `if (!confirm(...)) return` se hace cambiando a:
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({message: '¿Seguro?'}))) return;
 */

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AlertTriangle, HelpCircle, Info, ShieldAlert } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';

export type ConfirmTone = 'default' | 'warn' | 'danger' | 'info';

export interface ConfirmOptions {
  title?: string;
  /** Mensaje principal. Puede contener saltos de línea \n que se renderizan como párrafos. */
  message: string;
  /** Tono visual del icono / botón principal. */
  tone?: ConfirmTone;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Si true, oculta el botón cancelar (modo "OK" tipo alert). */
  alertOnly?: boolean;
}

const TONE_META: Record<
  ConfirmTone,
  {
    icon: typeof AlertTriangle;
    iconColor: string;
    ringColor: string;
    btnVariant: 'primary' | 'danger' | 'accent';
  }
> = {
  default: {
    icon: HelpCircle,
    iconColor: 'text-blue',
    ringColor: 'bg-blue-10/40',
    btnVariant: 'primary',
  },
  warn: {
    icon: AlertTriangle,
    iconColor: 'text-orange',
    ringColor: 'bg-orange/10',
    btnVariant: 'accent',
  },
  danger: {
    icon: ShieldAlert,
    iconColor: 'text-status-danger',
    ringColor: 'bg-status-danger/10',
    btnVariant: 'danger',
  },
  info: {
    icon: Info,
    iconColor: 'text-blue',
    ringColor: 'bg-blue-10/40',
    btnVariant: 'primary',
  },
};

interface ConfirmContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

/**
 * Provider global. Montar una vez en App.tsx envolviendo a toda la app.
 * Cualquier descendiente puede usar `useConfirm()` y recibir una función
 * que devuelve `Promise<boolean>` con el resultado del modal.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setState(opts);
    });
  }, []);

  function close(value: boolean) {
    setState(null);
    if (resolverRef.current) {
      resolverRef.current(value);
      resolverRef.current = null;
    }
  }

  const tone = state?.tone ?? 'default';
  const meta = TONE_META[tone];
  const Icon = meta.icon;
  const lineas = (state?.message ?? '').split(/\n+/);

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <Modal
        open={!!state}
        onClose={() => close(false)}
        size="sm"
        footer={
          <>
            {!state?.alertOnly && (
              <Button variant="secondary" onClick={() => close(false)}>
                {state?.cancelLabel ?? 'Cancelar'}
              </Button>
            )}
            <Button variant={meta.btnVariant} onClick={() => close(true)} autoFocus>
              {state?.confirmLabel ?? (state?.alertOnly ? 'Entendido' : 'Confirmar')}
            </Button>
          </>
        }
      >
        <div className="flex items-start gap-3">
          <div
            className={`grid h-10 w-10 flex-none place-items-center rounded-md ${meta.ringColor}`}
          >
            <Icon size={20} className={meta.iconColor} />
          </div>
          <div className="min-w-0 flex-1">
            {state?.title && (
              <h3 className="mb-1 text-base font-extrabold tracking-tight text-neutral-ink">
                {state.title}
              </h3>
            )}
            <div className="space-y-2 text-sm leading-relaxed text-neutral-muted">
              {lineas.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </ConfirmContext.Provider>
  );
}

/**
 * Hook para invocar el modal de confirmación.
 *
 * Ejemplo:
 *   const confirm = useConfirm();
 *   if (!(await confirm({ message: '¿Anular este movimiento?', tone: 'danger' }))) return;
 */
export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm() requires <ConfirmProvider> in the tree');
  }
  return ctx.confirm;
}

/**
 * Variante "alert" — solo botón OK. Útil para mensajes informativos
 * que no requieren decisión binaria.
 */
export function useAlert(): (
  opts: Omit<ConfirmOptions, 'alertOnly' | 'cancelLabel'>,
) => Promise<void> {
  const confirm = useConfirm();
  return useCallback(
    async (opts) => {
      await confirm({ ...opts, alertOnly: true });
    },
    [confirm],
  );
}
